import {isBrowser, isDevelopment} from "../../app/utils/read_environment_variables";
import {hitGoogleTagManager} from "../google_tag_manager/gtm_init";

export type AnalyticsEventParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Single dataLayer event name for all GA4 hits.
 * GTM: one Custom Event trigger on this name + GA4 Event tag with Event Name = {{event_name}}.
 */
export const GTM_EVENT = "hit_gtm_event";

const DEBUG_QUERY_PARAM = "debug_mode";
const DEBUG_STORAGE_KEY = "ga_debug_mode";

/**
 * GA4 DebugView: enable via ?debug_mode=1 or localStorage ga_debug_mode=1.
 * In development, debug_mode is always attached and events are logged to console.
 */
export const isAnalyticsDebugMode = (): boolean => {
    if (!isBrowser) {
        return false;
    }

    if (isDevelopment) {
        return true;
    }

    try {
        if (new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === "1") {
            return true;
        }

        if (window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1") {
            return true;
        }
    } catch {
        // ignore storage / URL access errors
    }

    return false;
};

const sanitizeParams = (params?: AnalyticsEventParams): Record<string, string | number | boolean> => {
    if (!params) {
        return {};
    }

    return Object.entries(params).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
        if (value === undefined || value === null || value === "") {
            return acc;
        }

        acc[key] = value;
        return acc;
    }, {});
};

/**
 * Single entry point for GA4 via GTM dataLayer.
 *
 * Always pushes:
 *   { event: "hit_gtm_event", event_name: "<ga4_name>", ...params }
 *
 * In GTM map `event_name` → GA4 Event Name so events appear as login / sign_up / etc. in GA4.
 * Safe when dataLayer / GTM is unavailable. Never pass PII.
 */
export const hitGtmEvent = (eventName: string, params?: AnalyticsEventParams): void => {
    if (!isBrowser || !eventName) {
        return;
    }

    const sanitized = sanitizeParams(params);
    const debugMode = isAnalyticsDebugMode();
    const payload = {
        event: GTM_EVENT,
        event_name: eventName,
        ...sanitized,
        ...(debugMode ? {debug_mode: true} : {})
    };

    if (isDevelopment || debugMode) {
        console.info("[GA4]", GTM_EVENT, {event_name: eventName, ...sanitized});
    }

    try {
        hitGoogleTagManager(payload);
    } catch {
        // never break UX because of analytics
    }
};

/** @deprecated Use hitGtmEvent — kept as alias for readability. */
export const trackEvent = hitGtmEvent;
